import i18next from 'i18next';
import {getBotsAndConnections, getConnections, getSimpleBots, getCookie, queueAccountAdsCacheInvalidation} from "./api";


import {ads_config, selectors} from "./config";
import {
    displayError,
    displayResults,
    getPaidFeatureIneligibleMessage,
    handleCheckboxes,
    isAdvertAdvertisingEligible,
    isAdvertActivatable,
    isAdvertBumpable,
    isAdvertCreatedByService,
    isAdvertDeactivatable,
    openSingleBumpConfirmationModal,
} from "./dom-manipulation";
import {
    getScopedAdsForTabVisibility,
    makeAccountsIds,
    mergeAdsWithOrphaned,
    normalizeAdStatus,
    processAccounts,
    processAds,
    refreshAdsBotConnections,
    sortConnections,
} from "./data-processing";
import {setBotsIdsFilter} from "./virtualselect";
import urls, {localizeAppPageUrl} from "../../common/js/urls";
import {withButtonPending} from "../../common/js/button-pending";
import {
    createBackupImageProxySession,
    copyOlxAdsToDrafts,
    fetchOlxAdvertBulkDetails,
    previewPlacementPlan,
    submitBumpOperation,
    submitPriceUpdate,
    submitStatusUpdate,
} from "./workspace-api";
import {downloadOlxAdsBackupZip} from './backup-export';
import {refreshWorkspaceData} from "./workspace";
import {
    bindPriceUpdateModalEvents,
    openPriceUpdateModal,
    resetPriceUpdateDatePicker,
    setPriceUpdateSelectValue,
} from "./price-update-modal";
import {
    getExecutionItemsFromPlacementPlan,
    renderPlacementPlan,
    PLACEMENT_OPERATION_ACTIVATE_ADS,
    PLACEMENT_OPERATION_ADVERTISE_ADS,
    PLACEMENT_OPERATION_BUMP_ADS,
} from "./placement-plan";
import {
    isPlacementOperationStorageError,
    storePlacementOperationPayload,
} from "./placement-operation-storage";
import {createOlxAccountPackagesModalController} from "../../common/js/olx-account-packages-modal";
import {isDateTimePickerValueInPast} from "../../common/js/date-time-picker";
import {escapeHtml} from "../../common/js/html";
import {customTemplate} from "../../common/js/dom-manipulation";
import {showToast} from "../../common/js/feature-toast";
import {collectErrorMessages} from "../../common/js/error-messages";
import {
    createPostingOperationAttemptTracker,
    getPostingOperationErrorMessage,
} from './posting-operation-attempt';
import {
    clearInventoryOperationReport,
    focusInventoryOperationReport,
    normalizeInventoryOperationResult,
    showInventoryItemStates,
    showInventoryOperationReport,
} from "./inventory-operation-feedback";

const adsAccountPackagesEnabled = window.adsAccountPackagesEnabled !== false;
const getAdsPlacementAccounts = () => adsAccountPackagesEnabled
    ? (ads_config.accounts || []).map((account) => ({
        id: String(account.id || account.account_id || ''),
        name: String(account.name || account.account_name || account.login || account.id || account.account_id || ''),
        status: String(account.status || 'WORKING'),
        is_active: account.is_active !== false && String(account.is_active ?? true).toLowerCase() !== 'false',
    }))
    : [];
let copyToDraftInProgress = false;
const copyToDraftAttempt = createPostingOperationAttemptTracker({
    storageKey: `adsPostingOperationAttempt:olx-copy:${window.activeOrganizationId || 'personal'}:v1`,
});
const bulkPriceUpdateAttempt = createPostingOperationAttemptTracker({
    storageKey: 'adsPostingOperationAttempt:bulk-price:v1',
});
const statusUpdateAttempt = createPostingOperationAttemptTracker({
    storageKey: 'adsPostingOperationAttempt:status-update:v1',
});
const singleBumpAttempt = createPostingOperationAttemptTracker({
    storageKey: 'adsPostingOperationAttempt:single-bump:v1',
});

const getSelectedAccountFilterIds = () => {
    const element = document.querySelector(selectors.accounts);
    if (!element) {
        return [];
    }
    if (typeof element.isAllSelected === 'function' && element.isAllSelected()) {
        return [];
    }
    if (typeof element.getSelectedOptions === 'function') {
        const selected = element.getSelectedOptions();
        const normalized = Array.isArray(selected) ? selected : Object.values(selected || {});
        return normalized
            .map((option) => String(option?.value || option || '').trim())
            .filter(Boolean);
    }
    const value = element.value;
    return Array.isArray(value)
        ? value.map((item) => String(item).trim()).filter(Boolean)
        : String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
};

const getContextualPlacementAccountId = () => {
    const selectedIds = getSelectedAccountFilterIds();
    if (selectedIds.length !== 1) {
        return '';
    }
    const availableAccounts = adsAccountPackagesController.getAvailablePlacementAccounts();
    const selectedId = String(selectedIds[0]);
    return availableAccounts.find((account) => String(account.id) === selectedId)?.id || '';
};

const refreshAdsPackagesLinkVisibility = () => {
    $('[data-ads-olx-packages-open]').prop(
        'hidden',
        adsAccountPackagesController.getAvailablePlacementAccounts().length === 0,
    );
};

const adsAccountPackagesController = createOlxAccountPackagesModalController({
    getAvailableAccounts: getAdsPlacementAccounts,
    getRequestHeaders: () => window.activeOrganizationId
        ? {'X-Organization-ID': window.activeOrganizationId}
        : {},
});

const ADS_FILTER_MOBILE_QUERY = '(max-width: 767.98px)';
let olxFilterDrawerSnapshot = null;
let olxFilterDrawerCommitted = false;

const getVirtualSelectValue = (selector, fallback = '') => {
    const element = document.querySelector(selector);
    if (!element) {
        return fallback;
    }
    if (typeof element.getValue === 'function') {
        return element.getValue();
    }
    if (typeof element.getNewValue === 'function') {
        return element.getNewValue();
    }
    if (typeof element.getNewValueMethod === 'function') {
        return element.getNewValueMethod();
    }
    if (typeof element.value !== 'undefined') {
        return element.value;
    }
    return $(selector).val() ?? fallback;
};

const setVirtualSelectValue = (selector, value) => {
    const element = document.querySelector(selector);
    if (!element) {
        return;
    }
    if (typeof element.setValue === 'function') {
        element.setValue(value);
        $(element).trigger('change');
        return;
    }
    if (typeof element.setValueMethod === 'function') {
        element.setValueMethod(value);
        $(element).trigger('change');
        return;
    }
    $(selector).val(value).trigger('change');
};

const getArrayFilterValue = (selector) => {
    const value = getVirtualSelectValue(selector, []);
    if (Array.isArray(value)) {
        return [...value];
    }
    return String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
};

const getOlxFilterSnapshot = () => ({
    accounts: getArrayFilterValue(selectors.accounts),
    botPresence: getVirtualSelectValue(selectors.botPresenceFilter, 'all') || 'all',
    botIds: getArrayFilterValue(selectors.botIdsFilter),
    search: String($('#search').val() || ''),
    categories: getArrayFilterValue(selectors.categories),
    locations: getArrayFilterValue(selectors.locations),
    sort: getVirtualSelectValue(selectors.sorting, '') || '',
});

const updateOlxFilterSummary = () => {
    const snapshot = getOlxFilterSnapshot();
    let count = 0;
    if (snapshot.accounts.length) count += 1;
    if (snapshot.botPresence && snapshot.botPresence !== 'all') count += 1;
    if (snapshot.botIds.length) count += 1;
    if (snapshot.search.trim()) count += 1;
    if (snapshot.categories.length) count += 1;
    if (snapshot.locations.length) count += 1;
    if (snapshot.sort) count += 1;

    $('[data-ads-filter-active-count="olx"]')
        .text(i18next.t('ads:scheduledActions.activeFiltersCount', {
            count,
            defaultValue: '{{count}} active',
        }))
        .toggleClass('d-none', count <= 0);

    const search = snapshot.search.trim();
    $('[data-ads-filter-search-chip="olx"]')
        .text(search ? i18next.t('ads:dom.searchChip', {query: search, defaultValue: `Search: ${search}`}) : '')
        .toggleClass('d-none', !search);
};

const restoreOlxFilterSnapshot = (snapshot) => {
    if (!snapshot) {
        return;
    }
    setVirtualSelectValue(selectors.accounts, snapshot.accounts || []);
    setVirtualSelectValue(selectors.botPresenceFilter, snapshot.botPresence || 'all');
    setVirtualSelectValue(selectors.botIdsFilter, snapshot.botIds || []);
    setVirtualSelectValue(selectors.categories, snapshot.categories || []);
    setVirtualSelectValue(selectors.locations, snapshot.locations || []);
    setVirtualSelectValue(selectors.sorting, snapshot.sort || '');
    $('#search').val(snapshot.search || '').trigger('input');
    updateOlxFilterSummary();
};

const resetOlxFilters = () => {
    restoreOlxFilterSnapshot({
        accounts: [],
        botPresence: 'all',
        botIds: [],
        search: '',
        categories: [],
        locations: [],
        sort: '',
    });
};

const syncOlxFilterPanelPlacement = () => {
    const card = document.querySelector('[data-ads-filter-card="olx"]');
    if (!card) {
        return;
    }
    const isMobile = window.matchMedia(ADS_FILTER_MOBILE_QUERY).matches;
    const target = document.querySelector(isMobile
        ? '[data-ads-filter-drawer-slot="olx"]'
        : '[data-ads-filter-inline-slot="olx"]');
    if (target && card.parentElement !== target) {
        target.appendChild(card);
    }
    updateOlxFilterSummary();
};

const bindOlxFilterDrawerEvents = () => {
    syncOlxFilterPanelPlacement();
    $(window)
        .off('resize.adsOlxFilters')
        .on('resize.adsOlxFilters', syncOlxFilterPanelPlacement);

    $('body').on('click', '[data-ads-filter-drawer-open="olx"]', function () {
        syncOlxFilterPanelPlacement();
    });

    $('#ads-olx-filter-drawer')
        .on('show.bs.modal', function () {
            syncOlxFilterPanelPlacement();
            olxFilterDrawerSnapshot = getOlxFilterSnapshot();
            olxFilterDrawerCommitted = false;
        })
        .on('hidden.bs.modal', function () {
            if (!olxFilterDrawerCommitted) {
                restoreOlxFilterSnapshot(olxFilterDrawerSnapshot);
                processAds();
            }
            olxFilterDrawerSnapshot = null;
            olxFilterDrawerCommitted = false;
        });

    $('body').on('click', '.ads-page-filter-apply[data-ads-filter-context="olx"]', function () {
        olxFilterDrawerCommitted = true;
        updateOlxFilterSummary();
        processAds();
        $('#ads-olx-filter-drawer').modal('hide');
    });

    $('body').on('click', '.ads-page-filter-reset[data-ads-filter-context="olx"]', function () {
        olxFilterDrawerCommitted = true;
        resetOlxFilters();
        processAds();
        $('#ads-olx-filter-drawer').modal('hide');
    });
};

const getSelectedCopyToDraftItems = () => {
    const items = [];
    $(selectors.adsList + ' input[type="checkbox"]:checked').each(function () {
        const adRow = $(this).closest('.inventory-row');
        const copyButton = adRow.find('.inventory-copy-to-draft');
        const accountId = Number(copyButton.data('account-id'));
        const adId = copyButton.data('inventory-id');
        if (accountId && adId) {
            items.push({account_id: accountId, ad_id: String(adId)});
        }
    });
    return items;
};

const getBackupImageUrl = (image) => (
    typeof image === 'string' ? image : String(image?.url || image?.image_url || '').trim()
);

const normalizeBackupImageItems = (images) => (
    (Array.isArray(images) ? images : [])
        .map((image) => ({
            url: getBackupImageUrl(image),
            name: String(image?.name || image?.filename || ''),
        }))
        .filter((image) => image.url)
);

const buildAdvertBackupItem = (ad) => ({
    account_id: Number(ad.account_id),
    ad_id: String(ad.id),
    title: String(ad.title || ''),
    images: normalizeBackupImageItems(ad.images),
});

const findAdvertByIds = ({accountId, adId}) => {
    const normalizedAdId = String(adId || '').trim();
    const normalizedAccountId = String(accountId || '').trim();
    if (!normalizedAdId) {
        return null;
    }
    const exactMatch = (ads_config.all_ads || []).find((ad) => (
        String(ad.id) === normalizedAdId &&
        (!normalizedAccountId || String(ad.account_id) === normalizedAccountId)
    ));
    if (exactMatch) {
        return exactMatch;
    }
    const sameIdMatches = (ads_config.all_ads || []).filter((ad) => String(ad.id) === normalizedAdId);
    return sameIdMatches.length === 1 ? sameIdMatches[0] : null;
};

const getBackupCandidateFromElement = ($element) => ({
    accountId: $element && $element.length ? $element.data('account-id') : '',
    adId: $element && $element.length ? $element.data('inventory-id') : '',
});

const getAdvertBackupItemFromRow = ($row, $preferredElement = $()) => {
    if (!$row || !$row.length) {
        return null;
    }
    const candidates = [
        getBackupCandidateFromElement($preferredElement),
    ];

    $row.find('[data-account-id][data-inventory-id]').each(function () {
        candidates.push(getBackupCandidateFromElement($(this)));
    });

    const checkboxAdId = $row.find('.inventory-checkbox input[type="checkbox"]').first().val();
    if (checkboxAdId) {
        candidates.push({accountId: '', adId: checkboxAdId});
    }

    const rowIdMatch = String($row.attr('id') || '').match(/^ad_id_(.+)$/);
    if (rowIdMatch) {
        candidates.push({accountId: '', adId: rowIdMatch[1]});
    }

    for (const candidate of candidates) {
        const advert = findAdvertByIds(candidate);
        if (advert) {
            return buildAdvertBackupItem(advert);
        }
    }
    return null;
};

const getSelectedAdvertBackupItems = () => $(selectors.adsList + ' .inventory-checkbox input[type="checkbox"]:checked').map(function () {
    return getAdvertBackupItemFromRow($(this).closest('.inventory-row'));
}).get().filter(Boolean);

const getSelectedAdvertItems = () => getSelectedCopyToDraftItems();

const getStatusUpdateEligibleItems = (operationType, selectedOnly = true) => {
    const source = selectedOnly
        ? $(selectors.adsList + ' input[type="checkbox"]:checked').map(function () {
            const row = $(this).closest('.inventory-row');
            const actionButton = row.find('.inventory-status-update, .inventory-copy-to-draft, .inventory-price-edit').first();
            const accountId = actionButton.data('account-id');
            const adId = actionButton.data('inventory-id') || $(this).val();
            return ads_config.all_ads.find((ad) => (
                String(ad.id) === String(adId) && String(ad.account_id) === String(accountId)
            ));
        }).get()
        : ads_config.all_ads;
    return source
        .filter((ad) => ad && (
            operationType === 'activate'
                ? isAdvertActivatable(ad)
                : isAdvertDeactivatable(ad)
        ))
        .map((ad) => ({
            account_id: Number(ad.account_id),
            ad_id: String(ad.id),
        }));
};

const getPaidFeatureBumpEligibleItems = (selectedOnly = true) => {
    const source = selectedOnly
        ? $(selectors.adsList + ' input[type="checkbox"]:checked').map(function () {
            const row = $(this).closest('.inventory-row');
            const actionButton = row.find('.inventory-paid-feature-bump, .inventory-copy-to-draft, .inventory-price-edit').first();
            const accountId = actionButton.data('account-id');
            const adId = actionButton.data('inventory-id') || $(this).val();
            return ads_config.all_ads.find((ad) => (
                String(ad.id) === String(adId) && String(ad.account_id) === String(accountId)
            ));
        }).get()
        : ads_config.all_ads;
    return source
        .filter((ad) => ad && isAdvertBumpable(ad))
        .map((ad) => ({
            account_id: Number(ad.account_id),
            ad_id: String(ad.id),
        }));
};

const getPaidFeatureAdvertisingEligibleItems = (selectedOnly = true) => {
    const source = selectedOnly
        ? $(selectors.adsList + ' input[type="checkbox"]:checked').map(function () {
            const row = $(this).closest('.inventory-row');
            const actionButton = row.find('.inventory-paid-feature-advertising, .inventory-copy-to-draft, .inventory-price-edit').first();
            const accountId = actionButton.data('account-id');
            const adId = $(this).val();
            return ads_config.all_ads.find((ad) => (
                String(ad.id) === String(adId) && String(ad.account_id) === String(accountId)
            ));
        }).get()
        : ads_config.all_ads;
    return source
        .filter((ad) => ad && isAdvertAdvertisingEligible(ad))
        .map((ad) => ({
            account_id: Number(ad.account_id),
            ad_id: String(ad.id),
        }));
};

const findAdvert = (accountId, adId) => ads_config.all_ads.find((ad) => (
    String(ad.id) === String(adId) && String(ad.account_id) === String(accountId)
));

const getSelectedPaidFeatureSourceAds = () => $(selectors.adsList + ' input[type="checkbox"]:checked').map(function () {
    const row = $(this).closest('.inventory-row');
    const actionButton = row.find('.inventory-paid-feature-bump, .inventory-paid-feature-advertising, .inventory-copy-to-draft, .inventory-price-edit').first();
    const accountId = actionButton.data('account-id');
    const adId = actionButton.data('inventory-id') || $(this).val();
    return ads_config.all_ads.find((ad) => (
        String(ad.id) === String(adId) && String(ad.account_id) === String(accountId)
    ));
}).get().filter(Boolean);

const getBulkPaidFeatureIneligibleMessage = (selectedAds) => {
    const externalAdvert = (selectedAds || []).find((ad) => ad && !isAdvertCreatedByService(ad));
    if (externalAdvert) {
        return getPaidFeatureIneligibleMessage(externalAdvert);
    }
    return i18next.t('ads:paidFeatures.selectEligibleAdsRequired', {
        defaultValue: 'Select at least one active advert.',
    });
};

const showUnavailablePaidFeatureTooltip = (element) => {
    if (!element) {
        return;
    }
    const tooltip = bootstrap.Tooltip.getOrCreateInstance(element, {
        trigger: 'manual',
        template: customTemplate,
    });
    tooltip.show();
    window.setTimeout(() => {
        tooltip.hide();
    }, 3000);
};

const clearPriceUpdateErrors = (modalSelector) => {
    $(`${modalSelector} .is-invalid`).removeClass('is-invalid');
    $(`${modalSelector} .invalid-feedback`).text('');
};

const showPriceUpdateFieldError = (fieldSelector, message) => {
    const field = $(fieldSelector);
    field.addClass('is-invalid');
    const datePicker = field.closest('[data-date-time-picker]');
    if (datePicker.length) {
        datePicker.addClass('is-invalid').find('.invalid-feedback').text(message);
        return;
    }
    field.siblings('.invalid-feedback').text(message);
};

const getScheduledAtIso = (modalSelector) => {
    const mode = $(`${modalSelector} .price-update-execution-mode`).val();
    if (mode !== 'scheduled') {
        return null;
    }
    const value = $(`${modalSelector} .price-update-scheduled-at`).val();
    if (!value) {
        return null;
    }
    return new Date(value).toISOString();
};

const buildExecutionPayload = (modalSelector) => {
    const executionMode = $(`${modalSelector} .price-update-execution-mode`).val() || 'now';
    const payload = {execution_mode: executionMode};
    if (executionMode === 'scheduled') {
        payload.scheduled_at = getScheduledAtIso(modalSelector);
    }
    return payload;
};

const applyPriceUpdateResponseToLocalAds = (response) => {
    const executions = Array.isArray(response && response.executions) ? response.executions : [];
    executions.forEach((execution) => {
        if (execution.status !== 'success' || !execution.price) {
            return;
        }
        const ad = ads_config.all_ads.find((item) => (
            String(item.id) === String(execution.ad_id)
            && String(item.account_id) === String(execution.account_id)
        ));
        if (!ad) {
            return;
        }
        ad.price = {...(ad.price || {}), ...execution.price};
        if (ad.price && Number.isFinite(Number(ad.price.value))) {
            ad.price.ua = ad.price.currency === 'UAH'
                ? Number(ad.price.value)
                : Number(ad.price.value) * Number((ads_config.currencies_rates || {})[ad.price.currency] || 1);
        }
    });
};

const enrichInventoryFeedbackItems = (items) => (Array.isArray(items) ? items : []).map((item) => {
    const advert = findAdvertByIds({accountId: item.account_id, adId: item.ad_id});
    return {
        ...item,
        title: String(item.title || advert?.title || '').trim(),
    };
});

const renderInventoryAcceptedResult = (result) => {
    if (result.severity === 'success') {
        clearInventoryOperationReport();
        return;
    }
    showInventoryOperationReport(result);
};

const hideOperationModal = (selector) => new Promise((resolve) => {
    const element = document.querySelector(`${selector}.show`) || document.querySelector(selector);
    if (!element) {
        resolve();
        return;
    }
    const isDisplayed = window.getComputedStyle(element).display !== 'none';
    if (!isDisplayed) {
        resolve();
        return;
    }
    const $element = $(element);
    let completed = false;
    let hideRequested = false;
    const cleanup = () => {
        element.removeEventListener('hidden.bs.modal', complete);
        element.removeEventListener('shown.bs.modal', requestHide);
        $element.off('hidden.bs.modal.inventoryFeedback', complete);
        $element.off('shown.bs.modal.inventoryFeedback', requestHide);
    };
    const complete = () => {
        if (completed) {
            return;
        }
        completed = true;
        cleanup();
        resolve();
    };
    let modal = null;
    const requestHide = () => {
        if (hideRequested || completed) {
            return;
        }
        hideRequested = true;
        if (modal) {
            modal.hide();
            return;
        }
        if (typeof $element.modal === 'function') {
            $element.modal('hide');
            return;
        }
        complete();
    };

    element.addEventListener('hidden.bs.modal', complete);
    $element.one('hidden.bs.modal.inventoryFeedback', complete);

    if (!element.classList.contains('show')) {
        return;
    }
    if (window.bootstrap?.Modal) {
        modal = window.bootstrap.Modal.getOrCreateInstance(element);
    }
    const transitionInstance = modal || $element.data('bs.modal');
    if (transitionInstance?._isTransitioning) {
        element.addEventListener('shown.bs.modal', requestHide);
        $element.one('shown.bs.modal.inventoryFeedback', requestHide);
        return;
    }
    requestHide();
});

const showInventoryRequestError = (error, formatter = null) => {
    const idempotencyMessage = getPostingOperationErrorMessage(error, i18next.t.bind(i18next));
    const message = idempotencyMessage || (formatter
        ? formatter(error)
        : collectErrorMessages(error?.errors || error).join(', '));
    showToast({
        message: message || i18next.t('ads:inventoryFeedback.genericFailure', {
            defaultValue: 'The operation failed for this item.',
        }),
        variant: 'danger',
    });
};

const displayPriceUpdateToast = (result) => {
    if (result.severity === 'success' && result.scheduled > 0) {
        showToast({
            message: i18next.t('ads:priceUpdate.scheduledSuccess', {
                defaultValue: 'Price update was scheduled for {{count}} ads.',
                count: result.scheduled,
            }),
            variant: 'info',
            action: {
                label: i18next.t('ads:placementAccounts.openScheduledActions', {defaultValue: 'Open scheduled actions'}),
                href: localizeAppPageUrl(urls.scheduledActionsPage),
            },
        });
        return;
    }
    if (result.severity === 'success') {
        showToast({
            message: i18next.t('ads:priceUpdate.success', {
                defaultValue: 'Updated {{count}} prices.',
                count: result.success,
            }),
            variant: 'success',
        });
    }
};

const handlePriceUpdateAcceptedResult = async (response, payload) => {
    const result = normalizeInventoryOperationResult({
        operationType: 'price_update',
        response,
        submittedItems: enrichInventoryFeedbackItems(payload.items),
    });
    renderInventoryAcceptedResult(result);
    displayPriceUpdateToast(result);
    await showInventoryItemStates(result.items);
    if (payload.execution_mode !== 'scheduled') {
        applyPriceUpdateResponseToLocalAds(response);
        processAds();
    }
    if (result.severity !== 'success') {
        focusInventoryOperationReport();
    }
    return result;
};

const applyStatusUpdateResponseToLocalAds = (response) => {
    const executions = Array.isArray(response && response.executions) ? response.executions : [];
    executions.forEach((execution) => {
        if (execution.status !== 'success' || !execution.new_status) {
            return;
        }
        const ad = ads_config.all_ads.find((item) => (
            String(item.id) === String(execution.ad_id)
            && String(item.account_id) === String(execution.account_id)
        ));
        if (!ad) {
            return;
        }
        ad.olx_status = execution.new_status;
        ad.status = normalizeAdStatus(execution.new_status);
    });
};

const displayStatusUpdateToast = (result, operationType) => {
    if (result.severity === 'success' && result.scheduled > 0) {
        showToast({
            message: i18next.t('ads:statusUpdate.scheduledSuccess', {
                defaultValue: 'Status update was scheduled for {{count}} ads.',
                count: result.scheduled,
            }),
            variant: 'info',
            action: {
                label: i18next.t('ads:placementAccounts.openScheduledActions', {defaultValue: 'Open scheduled actions'}),
                href: localizeAppPageUrl(urls.scheduledActionsPage),
            },
        });
        return;
    }
    if (result.severity === 'success') {
        showToast({
            message: i18next.t('ads:statusUpdate.success', {
                defaultValue: 'Updated statuses: {{count}}.',
                count: result.success,
                operation: operationType,
            }),
            variant: 'success',
        });
    }
};

const handleStatusUpdateAcceptedResult = async (response, payload) => {
    const result = normalizeInventoryOperationResult({
        operationType: `status_${payload.operation_type}`,
        response,
        submittedItems: enrichInventoryFeedbackItems(payload.items),
    });
    renderInventoryAcceptedResult(result);
    if (result.severity !== 'success') {
        focusInventoryOperationReport();
    }
    displayStatusUpdateToast(result, payload.operation_type);
    await showInventoryItemStates(result.items);
    if (payload.execution_mode !== 'scheduled') {
        applyStatusUpdateResponseToLocalAds(response);
        processAds();
    }
    await refreshWorkspaceData();
    return result;
};

const displayPriceUpdateServerErrors = (modalSelector, errors) => {
    if (!errors || typeof errors !== 'object') {
        return false;
    }
    let displayed = false;
    Object.entries(errors).forEach(([field, messages]) => {
        const message = Array.isArray(messages) ? messages.join(', ') : String(messages || '');
        if (!message) {
            return;
        }
        if (field === 'price.value' || field === 'price') {
            showPriceUpdateFieldError(`${modalSelector} #price-update-value`, message);
            displayed = true;
        }
        if (field === 'price.currency') {
            showPriceUpdateFieldError(`${modalSelector} #price-update-currency`, message);
            displayed = true;
        }
        if (field === 'adjustment.value') {
            showPriceUpdateFieldError(`${modalSelector} #bulk-price-update-value`, message);
            displayed = true;
        }
        if (field === 'scheduled_at' && modalSelector === '#bulk-price-update-modal') {
            showPriceUpdateFieldError('#bulk-price-update-scheduled-at', message);
            displayed = true;
        }
    });
    return displayed;
};

const displayStatusUpdateServerErrors = (errors) => {
    if (!errors || typeof errors !== 'object' || !Object.prototype.hasOwnProperty.call(errors, 'scheduled_at')) {
        return false;
    }
    const messages = errors.scheduled_at;
    const message = Array.isArray(messages) ? messages.join(', ') : String(messages || '');
    if (!message) {
        return false;
    }
    showPriceUpdateFieldError('#status-update-scheduled-at', message);
    return true;
};

const formatCopyToDraftError = (errors) => {
    return collectErrorMessages(errors).join(', ');
};

const renderWorkspaceWarning = (container, message) => {
    $(container)
        .empty()
        .append($('<div/>', {class: 'workspace-warning'}).text(message || ''));
};

const getSingleBumpModalItem = () => {
    const modalItem = $('#single-bump-confirmation-modal').data('single-bump-item') || {};
    const accountId = Number(modalItem.account_id);
    const adId = String(modalItem.ad_id || '').trim();
    if (!accountId || !adId) {
        return null;
    }
    return {account_id: accountId, ad_id: adId};
};

const formatSingleBumpError = (error) => {
    const idempotencyMessage = getPostingOperationErrorMessage(error, i18next.t.bind(i18next));
    if (idempotencyMessage) {
        return idempotencyMessage;
    }
    const message = formatCopyToDraftError(error && (error.errors || error));
    const normalized = message.toLowerCase();
    if (
        normalized.includes('not belongs to partner')
        || normalized.includes('no partner code')
        || normalized.includes('published through this service')
        || normalized.includes('published through pulsgpt')
        || normalized.includes('опубликованные через сервис')
        || normalized.includes('опубликованных через pulsgpt')
        || normalized.includes('опубліковані через сервіс')
        || normalized.includes('опублікованих через pulsgpt')
    ) {
        return i18next.t('ads:paidFeatures.serviceCreatedOnly', {
            defaultValue: 'Advertising and bump are available only for adverts published through PulsGPT.',
        });
    }
    return message || i18next.t('ads:placementOperation.unknownError', {defaultValue: 'Unknown error'});
};

const isAcceptedCopyToDraftResult = (value) => (
    value
    && Array.isArray(value.items)
    && Number.isFinite(Number(value.error_count))
);

const handleCopyToDraftAcceptedResult = async (response, items) => {
    const result = normalizeInventoryOperationResult({
        operationType: 'copy_to_drafts',
        response,
        submittedItems: enrichInventoryFeedbackItems(items),
    });
    renderInventoryAcceptedResult(result);
    if (result.severity !== 'success') {
        focusInventoryOperationReport();
    }
    if (result.severity === 'success') {
        showToast({
            message: i18next.t('ads:events.copyToDraftsSuccess', {
                defaultValue: 'Copied {{count}} ads to drafts.',
                count: result.success,
            }),
            variant: 'success',
        });
    }
    await showInventoryItemStates(result.items);
    await refreshWorkspaceData();
    return result;
};

const copySelectedOlxAdsToDrafts = async (items) => {
    if (copyToDraftInProgress) {
        return;
    }
    if (!items.length) {
        displayError(i18next.t('ads:events.copyToDraftsSelectAdsError', {defaultValue: 'Select at least one ad to copy.'}));
        return;
    }

    const buttons = $('.inventory-copy-to-draft, .copy-to-draft-button');
    const payload = {items};
    const idempotencyKey = copyToDraftAttempt.getKey(payload);
    copyToDraftInProgress = true;
    try {
        await withButtonPending(buttons, async () => {
            let response;
            try {
                response = await copyOlxAdsToDrafts(payload, idempotencyKey);
            } catch (error) {
                if (Number(error?.httpStatus ?? error?.status) !== 400 || !isAcceptedCopyToDraftResult(error)) {
                    copyToDraftAttempt.handleError(error);
                    throw error;
                }
                response = error;
            }
            copyToDraftAttempt.complete();
            await handleCopyToDraftAcceptedResult(response, items);
        });
    } catch (error) {
        showInventoryRequestError(error);
    } finally {
        copyToDraftInProgress = false;
        handleCheckboxes();
    }
};

const showBackupExportResult = (manifest, {extraFailedImages = 0} = {}) => {
    const failedRows = Number((manifest && manifest.failed_rows && manifest.failed_rows.length) || 0);
    if (failedRows > 0) {
        showToast({
            message: i18next.t('ads:backup.partialRowsWarning', {
                count: failedRows,
                defaultValue: 'Backup downloaded, but {{count}} adverts could not be included.',
            }),
            variant: 'warning',
        });
        return;
    }
    const failedImages = Number((manifest && manifest.failed_images && manifest.failed_images.length) || 0)
        + Number(extraFailedImages || 0);
    if (failedImages > 0) {
        showToast({
            message: i18next.t('ads:backup.partialImagesWarning', {
                count: failedImages,
                defaultValue: 'Backup downloaded, but {{count}} images could not be embedded. Their URLs were kept in CSV.',
            }),
            variant: 'warning',
        });
        return;
    }
    showToast({
        message: i18next.t('ads:backup.success', {defaultValue: 'Backup downloaded.'}),
        variant: 'success',
    });
};

const BACKUP_OPTION_CONFIG = {
    text: {
        includeImages: false,
        quality: 'standard',
    },
    images_standard: {
        includeImages: true,
        quality: 'standard',
    },
    images_max_2000: {
        includeImages: true,
        quality: 'max_2000',
    },
    images_max_4096: {
        includeImages: true,
        quality: 'max_4096',
    },
    images_max_8192: {
        includeImages: true,
        quality: 'max_8192',
    },
};

const BACKUP_DEFAULT_OPTION = 'text';
const BACKUP_PHASE_IDLE = 'idle';
const BACKUP_PHASE_PARTIAL_ERROR = 'partial_error';
const BACKUP_PHASE_EXPORTING = 'exporting';
const BACKUP_DEFAULT_LIMITS = {
    max_archive_bytes: 100 * 1024 * 1024,
    max_image_bytes: 5 * 1024 * 1024,
};

const advertBackupOptionsState = {
    items: [],
    detailByKey: {},
    detailErrorByKey: {},
    selectedOption: BACKUP_DEFAULT_OPTION,
    selectedImageUrlsByItem: {},
    phase: BACKUP_PHASE_IDLE,
    progressTotal: 0,
    progressCompleted: 0,
    progressFailed: 0,
    downloadErrorsByItem: {},
    proxyFailedImageCount: 0,
    partialDownload: null,
    triggerButton: null,
    proxyLimits: {...BACKUP_DEFAULT_LIMITS},
};

const getBackupItemKey = (item = {}) => `${Number(item.account_id) || ''}:${String(item.ad_id || item.id || '').trim()}`;

const getBackupItemByKey = (key) => advertBackupOptionsState.items.find((item) => getBackupItemKey(item) === key) || null;

const getSelectedBackupOptionConfig = () => (
    BACKUP_OPTION_CONFIG[advertBackupOptionsState.selectedOption] || BACKUP_OPTION_CONFIG[BACKUP_DEFAULT_OPTION]
);

const isAdvertBackupImageMode = () => getSelectedBackupOptionConfig().includeImages;

const isAdvertBackupBusy = () => advertBackupOptionsState.phase === BACKUP_PHASE_EXPORTING;

const getAdvertBackupOptionsModal = () => {
    const modal = document.getElementById('ads-backup-options-modal');
    if (!modal || !window.bootstrap?.Modal) {
        return null;
    }
    return window.bootstrap.Modal.getOrCreateInstance(modal);
};

const getBackupDetailForItem = (item) => advertBackupOptionsState.detailByKey[getBackupItemKey(item)] || null;

const getBackupItemTitle = (item) => {
    const detailTitle = getBackupDetailForItem(item)?.advert?.title;
    if (detailTitle) {
        return detailTitle;
    }
    if (item?.title) {
        return item.title;
    }
    const advert = findAdvertByIds({accountId: item.account_id, adId: item.ad_id});
    return advert?.title || `${item.ad_id}`;
};

const resetAdvertBackupDownloadState = ({phase = BACKUP_PHASE_IDLE, clearSelection = false, clearPartial = true} = {}) => {
    advertBackupOptionsState.phase = phase;
    advertBackupOptionsState.progressTotal = 0;
    advertBackupOptionsState.progressCompleted = 0;
    advertBackupOptionsState.progressFailed = 0;
    advertBackupOptionsState.downloadErrorsByItem = {};
    advertBackupOptionsState.proxyFailedImageCount = 0;
    if (clearSelection) {
        advertBackupOptionsState.selectedImageUrlsByItem = {};
    }
    if (clearPartial) {
        advertBackupOptionsState.partialDownload = null;
    }
};

const addAdvertBackupDownloadError = ({itemKey = '', title = '', count = 1} = {}) => {
    const key = String(itemKey || '').trim() || title || 'backup';
    const current = advertBackupOptionsState.downloadErrorsByItem[key] || {
        title: title || getBackupItemTitle(getBackupItemByKey(key) || {}),
        count: 0,
    };
    current.count += Number(count || 1);
    advertBackupOptionsState.downloadErrorsByItem[key] = current;
};

const getAdvertBackupGroupedErrorCount = () => Object.values(advertBackupOptionsState.downloadErrorsByItem)
    .reduce((total, item) => total + Number(item?.count || 0), 0);

const getAdvertBackupSelectedImageUrlsByItem = () => {
    const urlsByItem = {};
    Object.entries(advertBackupOptionsState.selectedImageUrlsByItem || {}).forEach(([key, selectedImages]) => {
        if (!Array.isArray(selectedImages)) {
            return;
        }
        const item = getBackupItemByKey(key);
        const title = item ? getBackupItemTitle(item) : '';
        const normalizedImages = selectedImages
            .map((image) => {
                if (typeof image === 'string') {
                    return {
                        url: image,
                        proxy_url: image,
                        original_url: image,
                        item_key: key,
                        title,
                    };
                }
                const proxyUrl = String(image?.proxy_url || image?.url || '').trim();
                if (!proxyUrl) {
                    return null;
                }
                return {
                    ...image,
                    url: proxyUrl,
                    proxy_url: proxyUrl,
                    original_url: String(image?.original_url || image?.fallback_url || image?.url || '').trim(),
                    item_key: key,
                    title: image?.title || title,
                };
            })
            .filter(Boolean);
        if (normalizedImages.length) {
            urlsByItem[key] = normalizedImages;
        }
    });
    return urlsByItem;
};

const renderBackupDownloadErrors = () => Object.values(advertBackupOptionsState.downloadErrorsByItem)
    .filter((item) => item && item.count > 0)
    .map((item) => `
        <li>${escapeHtml(i18next.t('ads:backup.preloadFailedItem', {
            title: item.title,
            count: item.count,
            defaultValue: '{{title}}: {{count}} images',
        }))}</li>
    `)
    .join('');

const renderAdvertBackupOptionsModal = () => {
    const modal = $('#ads-backup-options-modal');
    if (!modal.length) {
        return;
    }
    const itemCount = advertBackupOptionsState.items.length;
    const firstItem = advertBackupOptionsState.items[0];
    const isBusy = isAdvertBackupBusy();
    const isImageMode = isAdvertBackupImageMode();
    const hasPartialErrors = advertBackupOptionsState.phase === BACKUP_PHASE_PARTIAL_ERROR;
    const showProgress = isImageMode && (
        advertBackupOptionsState.phase === BACKUP_PHASE_EXPORTING ||
        hasPartialErrors
    );
    const showImagePanel = showProgress || hasPartialErrors;
    modal.find('[data-ads-backup-ad-title]').text(itemCount === 1 && firstItem ? getBackupItemTitle(firstItem) : '');
    modal.find('.ads-backup-option').each(function () {
        const button = $(this);
        const option = String(button.data('ads-backup-option') || '');
        const isActive = option === advertBackupOptionsState.selectedOption;
        button
            .toggleClass('is-active', isActive)
            .attr('aria-checked', isActive ? 'true' : 'false')
            .prop('disabled', isBusy);
        button.find('.ads-backup-option-check').attr('aria-hidden', isActive ? 'false' : 'true');
    });
    const submitLabelKey = advertBackupOptionsState.phase === BACKUP_PHASE_EXPORTING
        ? 'ads:backup.processing'
        : hasPartialErrors
            ? 'ads:backup.downloadAvailable'
            : 'ads:backup.confirmDownload';
    const submitLabelDefault = advertBackupOptionsState.phase === BACKUP_PHASE_EXPORTING
        ? 'Creating backup...'
        : hasPartialErrors
            ? 'Download available'
            : 'Download';
    modal.find('.ads-backup-confirm-download')
        .prop('disabled', isBusy)
        .text(i18next.t(submitLabelKey, {defaultValue: submitLabelDefault}));
    modal.find('[data-ads-backup-images-panel]').toggleClass('d-none', !showImagePanel);
    modal.find('[data-ads-backup-progress]').toggleClass('d-none', !showProgress);
    modal.find('[data-ads-backup-loading]').toggleClass('d-none', advertBackupOptionsState.phase !== BACKUP_PHASE_EXPORTING);
    const total = advertBackupOptionsState.progressTotal;
    const completed = advertBackupOptionsState.progressCompleted;
    const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;
    modal.find('[data-ads-backup-progress-text]').text(i18next.t('ads:backup.downloadProgress', {
        completed,
        total,
        defaultValue: 'Processed {{completed}} of {{total}}',
    }));
    modal.find('[data-ads-backup-progress-bar]').css('width', `${progressPercent}%`);
    modal.find('[data-ads-backup-progress-track]').attr('aria-valuenow', progressPercent);
    const errorsHtml = renderBackupDownloadErrors();
    modal.find('[data-ads-backup-no-images]').toggleClass('d-none', !showProgress || total > 0 || Boolean(errorsHtml));
    modal.find('[data-ads-backup-preload-errors]').toggleClass('d-none', !errorsHtml);
    modal.find('[data-ads-backup-preload-errors-list]').html(errorsHtml);
};

const resetAdvertBackupOptionsModal = () => {
    advertBackupOptionsState.items = [];
    advertBackupOptionsState.detailByKey = {};
    advertBackupOptionsState.detailErrorByKey = {};
    advertBackupOptionsState.selectedOption = BACKUP_DEFAULT_OPTION;
    advertBackupOptionsState.selectedImageUrlsByItem = {};
    advertBackupOptionsState.proxyLimits = {...BACKUP_DEFAULT_LIMITS};
    resetAdvertBackupDownloadState();
    advertBackupOptionsState.triggerButton = null;
    $('#ads-backup-options-modal [data-ads-backup-ad-title]').text('');
};

const openAdvertBackupOptionsModal = (items, triggerButton = null) => {
    if (!items.length) {
        displayError(i18next.t('ads:backup.selectRequired', {defaultValue: 'Select at least one item.'}));
        return;
    }

    advertBackupOptionsState.items = [...items];
    advertBackupOptionsState.detailByKey = {};
    advertBackupOptionsState.detailErrorByKey = {};
    advertBackupOptionsState.selectedOption = BACKUP_DEFAULT_OPTION;
    advertBackupOptionsState.selectedImageUrlsByItem = {};
    advertBackupOptionsState.proxyLimits = {...BACKUP_DEFAULT_LIMITS};
    resetAdvertBackupDownloadState();
    advertBackupOptionsState.triggerButton = triggerButton;
    renderAdvertBackupOptionsModal();
    getAdvertBackupOptionsModal()?.show();
};

const loadAdvertBackupProxyImages = async () => {
    const items = advertBackupOptionsState.items.map((item) => ({
        account_id: item.account_id,
        ad_id: item.ad_id,
    }));
    try {
        advertBackupOptionsState.selectedImageUrlsByItem = {};
        advertBackupOptionsState.proxyLimits = {...BACKUP_DEFAULT_LIMITS};
        const response = await createBackupImageProxySession({
            items,
            quality: getSelectedBackupOptionConfig().quality,
        });
        advertBackupOptionsState.proxyLimits = {
            ...BACKUP_DEFAULT_LIMITS,
            ...(response.limits || {}),
        };
        (response.images || []).forEach((image) => {
            const key = getBackupItemKey(image);
            if (!Array.isArray(advertBackupOptionsState.selectedImageUrlsByItem[key])) {
                advertBackupOptionsState.selectedImageUrlsByItem[key] = [];
            }
            advertBackupOptionsState.selectedImageUrlsByItem[key].push({
                url: image.proxy_url,
                proxy_url: image.proxy_url,
                original_url: image.original_url,
                item_key: key,
                title: image.title || getBackupItemTitle(getBackupItemByKey(key) || image),
            });
        });
        (response.failed_items || []).forEach((item) => {
            advertBackupOptionsState.proxyFailedImageCount += 1;
            addAdvertBackupDownloadError({
                itemKey: getBackupItemKey(item),
                title: item.title || getBackupItemTitle(item),
                count: 1,
            });
        });
    } catch (error) {
        advertBackupOptionsState.proxyFailedImageCount += advertBackupOptionsState.items.length;
        advertBackupOptionsState.items.forEach((item) => {
            addAdvertBackupDownloadError({
                itemKey: getBackupItemKey(item),
                title: getBackupItemTitle(item),
                count: 1,
            });
        });
        console.error('[ads-backup] Unable to create image proxy session for backup', {error, items});
    }
};

const setAdvertBackupOption = (option) => {
    if (!Object.prototype.hasOwnProperty.call(BACKUP_OPTION_CONFIG, option)) {
        return;
    }
    if (advertBackupOptionsState.selectedOption === option || isAdvertBackupBusy()) {
        return;
    }
    advertBackupOptionsState.selectedOption = option;
    advertBackupOptionsState.selectedImageUrlsByItem = {};
    resetAdvertBackupDownloadState();
    renderAdvertBackupOptionsModal();
};

const loadAdvertBackupDetails = async (items) => {
    const missingItemsByKey = {};
    (items || []).forEach((item) => {
        const key = getBackupItemKey(item);
        if (!key || advertBackupOptionsState.detailByKey[key] || advertBackupOptionsState.detailErrorByKey[key]) {
            return;
        }
        missingItemsByKey[key] = {
            account_id: Number(item.account_id),
            ad_id: String(item.ad_id || item.id || '').trim(),
        };
    });

    const missingItems = Object.values(missingItemsByKey).filter((item) => item.account_id && item.ad_id);
    if (!missingItems.length) {
        return;
    }

    const response = await fetchOlxAdvertBulkDetails(missingItems);
    (response.items || []).forEach((item) => {
        const key = getBackupItemKey(item);
        if (!key || !item.detail) {
            return;
        }
        advertBackupOptionsState.detailByKey[key] = item.detail;
        delete advertBackupOptionsState.detailErrorByKey[key];
    });
    (response.failed_items || []).forEach((item) => {
        const key = getBackupItemKey(item);
        if (!key) {
            return;
        }
        advertBackupOptionsState.detailErrorByKey[key] = item;
    });
};

const downloadAdvertBackupPartialArtifact = () => {
    const partialDownload = advertBackupOptionsState.partialDownload;
    if (!partialDownload || typeof partialDownload.download !== 'function') {
        return false;
    }
    partialDownload.download();
    getAdvertBackupOptionsModal()?.hide();
    showBackupExportResult(partialDownload.manifest, {
        extraFailedImages: advertBackupOptionsState.proxyFailedImageCount,
    });
    return true;
};

const updateAdvertBackupImageProgress = ({completed, total, failed}) => {
    advertBackupOptionsState.progressCompleted = Number(completed || 0);
    advertBackupOptionsState.progressTotal = Number(total || 0);
    advertBackupOptionsState.progressFailed = Number(failed || 0);
    renderAdvertBackupOptionsModal();
};

const handleAdvertBackupImageFailed = ({itemKey = '', title = ''}) => {
    addAdvertBackupDownloadError({itemKey, title, count: 1});
};

const exportAdvertBackupFromOptionsModal = async () => {
    const items = advertBackupOptionsState.items;
    const includeImages = isAdvertBackupImageMode();
    const confirmButton = $('#ads-backup-options-modal .ads-backup-confirm-download');
    advertBackupOptionsState.phase = BACKUP_PHASE_EXPORTING;
    advertBackupOptionsState.selectedImageUrlsByItem = {};
    resetAdvertBackupDownloadState({phase: BACKUP_PHASE_EXPORTING});
    renderAdvertBackupOptionsModal();
    try {
        await withButtonPending(confirmButton, async () => {
            if (includeImages) {
                await loadAdvertBackupProxyImages();
                renderAdvertBackupOptionsModal();
            }
            const selectedImageUrlsByItem = includeImages
                ? getAdvertBackupSelectedImageUrlsByItem()
                : {};
            await loadAdvertBackupDetails(items);
            const result = await downloadOlxAdsBackupZip(items, {
                detailByItemKey: advertBackupOptionsState.detailByKey,
                detailErrorByItemKey: advertBackupOptionsState.detailErrorByKey,
                includeImages,
                selectedImageUrlsByItem,
                deferDownload: includeImages,
                onImageProgress: updateAdvertBackupImageProgress,
                onImageFailed: handleAdvertBackupImageFailed,
                maxArchiveBytes: advertBackupOptionsState.proxyLimits.max_archive_bytes,
                maxImageBytes: advertBackupOptionsState.proxyLimits.max_image_bytes,
            });
            const manifest = result?.manifest || result;
            const failedCount = getAdvertBackupGroupedErrorCount();
            if (includeImages && failedCount > 0) {
                advertBackupOptionsState.partialDownload = result?.download ? result : null;
                advertBackupOptionsState.phase = BACKUP_PHASE_PARTIAL_ERROR;
                renderAdvertBackupOptionsModal();
                return;
            }
            if (includeImages && result?.download) {
                result.download();
            }
            getAdvertBackupOptionsModal()?.hide();
            showBackupExportResult(manifest);
        });
        renderAdvertBackupOptionsModal();
    } catch (error) {
        advertBackupOptionsState.phase = error?.code !== 'archive_size_limit'
            && includeImages
            && getAdvertBackupGroupedErrorCount() > 0
            ? BACKUP_PHASE_PARTIAL_ERROR
            : BACKUP_PHASE_IDLE;
        renderAdvertBackupOptionsModal();
        if (error?.code !== 'archive_size_limit') {
            console.error('[ads-backup] Unable to create active advert backup from options modal', {
                error,
                items,
                includeImages,
                selectedImageUrlsByItem: advertBackupOptionsState.selectedImageUrlsByItem,
                failedRows: error?.failedRows || [],
            });
        }
        showToast({
            message: i18next.t('ads:backup.error', {defaultValue: 'Unable to create backup.'}),
            variant: 'danger',
        });
    }
};

const submitAdvertBackupOptionsModal = async () => {
    if (!advertBackupOptionsState.items.length) {
        showToast({
            message: i18next.t('ads:backup.error', {defaultValue: 'Unable to create backup.'}),
            variant: 'danger',
        });
        return;
    }
    if (isAdvertBackupBusy()) {
        return;
    }
    if (advertBackupOptionsState.phase === BACKUP_PHASE_PARTIAL_ERROR && downloadAdvertBackupPartialArtifact()) {
        return;
    }
    await exportAdvertBackupFromOptionsModal();
};

const buildBulkPriceUpdatePayload = () => ({
    ...buildExecutionPayload('#bulk-price-update-modal'),
    operation_type: $('#bulk-price-update-operation').val(),
    items: getSelectedAdvertItems(),
    adjustment: {
        value: Number($('#bulk-price-update-value').val()),
    },
});

const validateBulkPriceUpdatePayload = (payload) => {
    clearPriceUpdateErrors('#bulk-price-update-modal');
    if (!payload.items.length) {
        displayError(i18next.t('ads:priceUpdate.selectAdsRequired', {defaultValue: 'Select at least one advert.'}));
        return false;
    }
    if (payload.execution_mode === 'scheduled' && !payload.scheduled_at) {
        showPriceUpdateFieldError('#bulk-price-update-scheduled-at', i18next.t('ads:priceUpdate.scheduledAtRequired', {defaultValue: 'Select scheduled time.'}));
        return false;
    }
    if (payload.execution_mode === 'scheduled' && isDateTimePickerValueInPast('#bulk-price-update-scheduled-at')) {
        showPriceUpdateFieldError('#bulk-price-update-scheduled-at', i18next.t('ads:placementSchedule.pastTime', {defaultValue: 'Scheduled time must be in the future.'}));
        return false;
    }
    const value = Number(payload.adjustment.value);
    if (!Number.isFinite(value) || value < 0.01) {
        showPriceUpdateFieldError('#bulk-price-update-value', i18next.t('ads:priceUpdate.adjustmentInvalid', {defaultValue: 'Enter a value greater than zero.'}));
        return false;
    }
    if (String(payload.operation_type).includes('percent') && value > 500) {
        showPriceUpdateFieldError('#bulk-price-update-value', i18next.t('ads:priceUpdate.percentTooHigh', {defaultValue: 'Percent cannot be greater than 500.'}));
        return false;
    }
    return true;
};

const handleBulkPriceUpdateSubmit = async function (event) {
    event.preventDefault();
    const payload = buildBulkPriceUpdatePayload();
    if (!validateBulkPriceUpdatePayload(payload)) {
        return;
    }
    const submitButton = $('#bulk-price-update-modal button[type="submit"]');
    const idempotencyKey = bulkPriceUpdateAttempt.getKey(payload);
    let response;
    try {
        response = await withButtonPending(
            submitButton,
            () => submitPriceUpdate(payload, idempotencyKey),
        );
    } catch (error) {
        bulkPriceUpdateAttempt.handleError(error);
        if (!displayPriceUpdateServerErrors('#bulk-price-update-modal', error.errors || error)) {
            showInventoryRequestError(error);
        }
        return;
    }
    bulkPriceUpdateAttempt.complete();
    try {
        await hideOperationModal('#bulk-price-update-modal');
        await handlePriceUpdateAcceptedResult(response, payload);
    } catch (renderError) {
        console.error('Accepted bulk price update feedback failed.', renderError);
    }
};

const getStatusUpdateOperationLabel = (operationType) => (
    operationType === 'activate'
        ? i18next.t('ads:statusUpdate.activate', {defaultValue: 'Activate'})
        : i18next.t('ads:statusUpdate.deactivate', {defaultValue: 'Deactivate'})
);

const PLACEMENT_OPERATION_PATHS = {
    [PLACEMENT_OPERATION_ACTIVATE_ADS]: 'activate',
    [PLACEMENT_OPERATION_BUMP_ADS]: 'bump',
    [PLACEMENT_OPERATION_ADVERTISE_ADS]: 'advertising',
};
let placementOperationOpenFailureToast = null;

const openPlacementOperationPage = (payload) => {
    storePlacementOperationPayload({
        ...payload,
        return_url: window.location.href,
    });
    const operationPath = PLACEMENT_OPERATION_PATHS[payload.operation_type] || 'activate';
    window.location.href = localizeAppPageUrl(urls.placementOperationPage(operationPath));
};

const tryOpenPlacementOperationPage = (payload) => {
    try {
        openPlacementOperationPage(payload);
        return true;
    } catch (error) {
        if (!isPlacementOperationStorageError(error)) {
            throw error;
        }
        placementOperationOpenFailureToast?.dismiss();
        placementOperationOpenFailureToast = showToast({
            message: i18next.t('ads:placementOperation.openFailed', {
                defaultValue: 'The operation could not be opened. Please try again.',
            }),
            variant: 'danger',
        });
        return false;
    }
};

const statusPlacementState = {
    plan: null,
    filter: 'all',
    excludedItemKeys: [],
};

const renderStatusPlacementPlan = () => {
    const plan = statusPlacementState.plan;
    $('#status-update-modal .status-update-placement-plan').html(renderPlacementPlan(plan, {
        activeFilter: statusPlacementState.filter,
        allowExclude: true,
    }));
    const readyCount = Number(plan?.total_ready || 0);
    $('#status-update-modal button[type="submit"]').prop('disabled', readyCount === 0);
    $('#status-update-modal .status-update-ready-count').text(i18next.t('ads:placementPlan.readyFooter', {
        defaultValue: 'Ready to run: {{count}}',
        count: readyCount,
    }));
};

const loadStatusPlacementPlan = async () => {
    const operationType = $('#status-update-operation-type').val();
    if (operationType !== 'activate') {
        $('#status-update-modal .status-update-placement-plan').html('');
        $('#status-update-modal button[type="submit"]').prop('disabled', false);
        return;
    }
    const payload = {
        ...buildExecutionPayload('#status-update-modal'),
        operation_type: PLACEMENT_OPERATION_ACTIVATE_ADS,
        slot_source: 'paid_packet',
        items: $('#status-update-modal').data('status-update-items') || [],
        excluded_item_keys: statusPlacementState.excludedItemKeys,
    };
    $('#status-update-modal .status-update-placement-plan').html(
        `<div class="workspace-empty">${i18next.t('ads:placementPlan.loading', {defaultValue: 'Checking OLX placements...'})}</div>`,
    );
    const response = await previewPlacementPlan(payload);
    statusPlacementState.plan = response.preview;
    renderStatusPlacementPlan();
};

const openStatusUpdateModal = (operationType, items) => {
    if (!items.length) {
        displayError(i18next.t('ads:statusUpdate.selectAdsRequired', {defaultValue: 'Select at least one suitable advert.'}));
        return;
    }
    if (operationType === 'activate') {
        tryOpenPlacementOperationPage({
            operation_type: PLACEMENT_OPERATION_ACTIVATE_ADS,
            execution_mode: 'now',
            slot_source: 'paid_packet',
            items,
            allow_scheduled: true,
        });
        return;
    }
    const operationLabel = getStatusUpdateOperationLabel(operationType);
    $('#status-update-operation-type').val(operationType);
    $('#status-update-title').text(operationLabel);
    $('#status-update-modal .status-update-summary').text(i18next.t('ads:statusUpdate.summary', {
        defaultValue: '{{operation}} {{count}} ads.',
        operation: operationLabel,
        count: items.length,
    }));
    setPriceUpdateSelectValue('#status-update-execution-mode', 'now');
    resetPriceUpdateDatePicker('#status-update-scheduled-at');
    $('#status-update-modal .price-update-scheduled-row').addClass('d-none');
    statusPlacementState.plan = null;
    statusPlacementState.filter = 'all';
    statusPlacementState.excludedItemKeys = [];
    $('#status-update-modal .status-update-placement-plan').html('');
    $('#status-update-modal .status-update-ready-count').text('');
    $('#status-update-modal .status-update-refresh-plan').toggleClass('d-none', operationType !== 'activate');
    $('#status-update-modal').data('status-update-items', items).modal('show');
    if (operationType === 'activate') {
        loadStatusPlacementPlan()
            .catch((error) => {
                renderWorkspaceWarning(
                    $('#status-update-modal .status-update-placement-plan'),
                    formatCopyToDraftError(error.errors || error),
                );
            });
    }
};

const buildStatusUpdatePayload = () => {
    const operationType = $('#status-update-operation-type').val();
    const placementItems = operationType === 'activate' && statusPlacementState.plan
        ? getExecutionItemsFromPlacementPlan(statusPlacementState.plan)
        : ($('#status-update-modal').data('status-update-items') || []);
    return {
        ...buildExecutionPayload('#status-update-modal'),
        operation_type: operationType,
        slot_source: operationType === 'activate' ? 'paid_packet' : undefined,
        items: placementItems,
        excluded_item_keys: statusPlacementState.excludedItemKeys,
    };
};

const validateStatusUpdatePayload = (payload) => {
    clearPriceUpdateErrors('#status-update-modal');
    if (!payload.items.length) {
        displayError(i18next.t('ads:statusUpdate.selectAdsRequired', {defaultValue: 'Select at least one suitable advert.'}));
        return false;
    }
    if (payload.execution_mode === 'scheduled' && !payload.scheduled_at) {
        showPriceUpdateFieldError('#status-update-scheduled-at', i18next.t('ads:priceUpdate.scheduledAtRequired', {defaultValue: 'Select scheduled time.'}));
        return false;
    }
    if (payload.execution_mode === 'scheduled' && isDateTimePickerValueInPast('#status-update-scheduled-at')) {
        showPriceUpdateFieldError('#status-update-scheduled-at', i18next.t('ads:placementSchedule.pastTime', {defaultValue: 'Scheduled time must be in the future.'}));
        return false;
    }
    return true;
};

const handleStatusUpdateSubmit = async function (event) {
    event.preventDefault();
    const payload = buildStatusUpdatePayload();
    if (!validateStatusUpdatePayload(payload)) {
        return;
    }
    const submitButton = $('#status-update-modal button[type="submit"]');
    const idempotencyKey = statusUpdateAttempt.getKey(payload);
    let response;
    try {
        response = await withButtonPending(
            submitButton,
            () => submitStatusUpdate(payload, idempotencyKey),
        );
    } catch (error) {
        statusUpdateAttempt.handleError(error);
        if (!displayStatusUpdateServerErrors(error.errors || error)) {
            showInventoryRequestError(error);
        }
        return;
    }
    statusUpdateAttempt.complete();
    try {
        await hideOperationModal('#status-update-modal');
        await handleStatusUpdateAcceptedResult(response, payload);
    } catch (renderError) {
        console.error('Accepted status update feedback failed.', renderError);
    } finally {
        handleCheckboxes();
    }
};

export function handleEvents() {
    if (adsAccountPackagesEnabled) {
        adsAccountPackagesController.bindEvents();
    }
    refreshAdsPackagesLinkVisibility();
    bindOlxFilterDrawerEvents();

    $(document)
        .off('click.adsMobileBulkMenu')
        .on('click.adsMobileBulkMenu', function (event) {
            if ($(event.target).closest('.ads-mobile-bulk-more').length) {
                return;
            }
            $('.ads-mobile-bulk-more[open]').prop('open', false);
        })
        .off('keydown.adsMobileBulkMenu')
        .on('keydown.adsMobileBulkMenu', function (event) {
            if (event.key === 'Escape') {
                $('.ads-mobile-bulk-more[open]').prop('open', false);
            }
        });

    bindPriceUpdateModalEvents({
        findAdvert,
        displayError: showInventoryRequestError,
        onRequestError: showInventoryRequestError,
        onSuccess: (response, payload) => handlePriceUpdateAcceptedResult(response, payload),
        onReport: null,
    });

    function getOrphanedDisconnectData() {
        return getScopedAdsForTabVisibility(ads_config.all_ads)
            .filter(ad => ad.is_orphaned && ad.connection_id && ad.id)
            .map(ad => ({
                connection_id: ad.connection_id,
                ad_id: ad.id
            }));
    }

    $("#sorting").on("change", function (event) {
        updateOlxFilterSummary();
        processAds(event);
    })

    $("#accounts-select").on("change", function (event) {
        updateOlxFilterSummary();
        processAds(event);
    })

    $('body').on('click', '[data-ads-olx-packages-open]', function () {
        if (!adsAccountPackagesEnabled) {
            return;
        }
        const selectedAccountId = getContextualPlacementAccountId();
        const availableCount = adsAccountPackagesController.getAvailablePlacementAccounts().length;
        adsAccountPackagesController.openPackagesModal({
            accountId: selectedAccountId,
            autoSelectAccount: Boolean(selectedAccountId) || availableCount <= 1,
        });
    });

    $("#adds_state").on("change", function (event) {
        processAds(event);
    })

    $('body').on('click', '.orphaned-tab-link', function (event) {
        event.preventDefault();
        $('#tab-btn-6').prop('checked', true).trigger('change');
    })

    $('body').on('click', '.orphaned-disconnect-all-btn', function (event) {
        event.preventDefault();
        ads_config.dataToDisconnectBulk = getOrphanedDisconnectData();
    })

    $('body').on('click', '.inventory-copy-to-draft', function (event) {
        event.preventDefault();
        const button = $(this);
        const accountId = Number(button.data('account-id'));
        const adId = button.data('inventory-id');
        copySelectedOlxAdsToDrafts(
            accountId && adId ? [{account_id: accountId, ad_id: String(adId)}] : [],
        );
    })

    $('body').on('click', '.copy-to-draft-button', function (event) {
        event.preventDefault();
        copySelectedOlxAdsToDrafts(getSelectedCopyToDraftItems());
    })

    $('body').on('click', '.inventory-download-backup', function (event) {
        event.preventDefault();
        const button = $(this);
        const backupItem = getAdvertBackupItemFromRow(button.closest('.inventory-row'), button);
        openAdvertBackupOptionsModal(backupItem ? [backupItem] : [], button);
    })

    $('body').on('click', '.bulk-download-backup-button', function (event) {
        event.preventDefault();
        openAdvertBackupOptionsModal(getSelectedAdvertBackupItems(), this);
    })

    $('body').on('click', '.ads-backup-option', function (event) {
        event.preventDefault();
        if ($(this).prop('disabled')) {
            return;
        }
        setAdvertBackupOption(String($(this).data('ads-backup-option') || BACKUP_DEFAULT_OPTION));
    })

    $('body').on('submit', '#ads-backup-options-form', function (event) {
        event.preventDefault();
        submitAdvertBackupOptionsModal();
    })

    $('body').on('hidden.bs.modal', '#ads-backup-options-modal', resetAdvertBackupOptionsModal)

    $('body').on('click', '.inventory-price-edit, .inventory-price-edit-trigger', function (event) {
        event.preventDefault();
        const button = $(this);
        openPriceUpdateModal(Number(button.data('account-id')), button.data('inventory-id'));
    })

    $('body').on('click', '.inventory-status-update', function (event) {
        event.preventDefault();
        const button = $(this);
        const operationType = button.data('operation-type');
        const accountId = Number(button.data('account-id'));
        const adId = button.data('inventory-id');
        openStatusUpdateModal(
            operationType,
            accountId && adId ? [{account_id: accountId, ad_id: String(adId)}] : [],
        );
    })

    $('body').on('click', '.inventory-paid-feature-bump', function (event) {
        event.preventDefault();
        const button = $(this);
        if (button.attr('aria-disabled') === 'true') {
            showUnavailablePaidFeatureTooltip(this);
            return;
        }
        const accountId = Number(button.data('account-id'));
        const adId = button.data('inventory-id');
        const advert = findAdvert(accountId, adId);
        const items = accountId && adId && isAdvertBumpable(advert) ? [{account_id: accountId, ad_id: String(adId)}] : [];
        if (!items.length) {
            displayError(getPaidFeatureIneligibleMessage(advert));
            return;
        }
        openSingleBumpConfirmationModal({advert, accountId, adId});
    })

    $('body').on('click', '.single-bump-schedule', function (event) {
        event.preventDefault();
        const item = getSingleBumpModalItem();
        if (!item) {
            displayError(i18next.t('ads:paidFeatures.selectEligibleAdsRequired', {
                defaultValue: 'Select at least one active advert.',
            }));
            return;
        }
        if (tryOpenPlacementOperationPage({
            operation_type: PLACEMENT_OPERATION_BUMP_ADS,
            execution_mode: 'scheduled',
            items: [item],
            allow_scheduled: true,
        })) {
            $('#single-bump-confirmation-modal').modal('hide');
        }
    })

    $('body').on('click', '.single-bump-now', async function (event) {
        event.preventDefault();
        const button = $(this);
        const item = getSingleBumpModalItem();
        if (!item) {
            displayError(i18next.t('ads:paidFeatures.selectEligibleAdsRequired', {
                defaultValue: 'Select at least one active advert.',
            }));
            return;
        }
        const items = [item];
        const payload = {
            operation_type: PLACEMENT_OPERATION_BUMP_ADS,
            execution_mode: 'now',
            items,
        };
        const idempotencyKey = singleBumpAttempt.getKey(payload);
        let response;
        try {
            response = await withButtonPending(
                button,
                () => submitBumpOperation(payload, idempotencyKey),
            );
        } catch (error) {
            singleBumpAttempt.handleError(error);
            showInventoryRequestError(error, formatSingleBumpError);
            handleCheckboxes();
            return;
        }
        singleBumpAttempt.complete();

        let result;
        try {
            result = normalizeInventoryOperationResult({
                operationType: 'bump',
                response,
                submittedItems: enrichInventoryFeedbackItems(items),
            });
        } catch (renderError) {
            console.error('Accepted single bump result normalization failed.', renderError);
            handleCheckboxes();
            return;
        }
        const acceptedSteps = [
            ['modal', () => hideOperationModal('#single-bump-confirmation-modal')],
            ['report', () => renderInventoryAcceptedResult(result)],
            ['toast', () => {
                if (result.severity === 'success') {
                    showToast({
                        message: i18next.t('ads:paidFeatures.bumpStarted', {
                            defaultValue: 'Advert bump was started.',
                        }),
                        variant: 'success',
                    });
                }
            }],
            ['item states', () => showInventoryItemStates(result.items)],
            ['refresh', async () => {
                if (result.success > 0) {
                    queueAccountAdsCacheInvalidation(items.map((bumpItem) => bumpItem.account_id));
                    await processAccounts(makeAccountsIds(ads_config.accounts));
                    await refreshWorkspaceData();
                }
            }],
            ['focus', () => {
                if (result.severity !== 'success') {
                    focusInventoryOperationReport();
                }
            }],
        ];
        for (const [stepName, step] of acceptedSteps) {
            try {
                await step();
            } catch (renderError) {
                console.error(`Accepted single bump ${stepName} failed.`, renderError);
            }
        }
        handleCheckboxes();
    })

    $('body').on('click', '.inventory-paid-feature-advertising', function (event) {
        event.preventDefault();
        const button = $(this);
        if (button.attr('aria-disabled') === 'true') {
            showUnavailablePaidFeatureTooltip(this);
            return;
        }
        const accountId = Number(button.data('account-id'));
        const adId = button.data('inventory-id');
        const advert = findAdvert(accountId, adId);
        const items = accountId && adId && isAdvertAdvertisingEligible(advert) ? [{account_id: accountId, ad_id: String(adId)}] : [];
        if (!items.length) {
            displayError(getPaidFeatureIneligibleMessage(advert));
            return;
        }
        tryOpenPlacementOperationPage({
            operation_type: PLACEMENT_OPERATION_ADVERTISE_ADS,
            execution_mode: 'now',
            items,
            allow_scheduled: true,
        });
    })

    $('body').on('click', '.bulk-status-activate-button, .bulk-status-deactivate-button', function (event) {
        event.preventDefault();
        const operationType = $(this).data('operation-type');
        openStatusUpdateModal(operationType, getStatusUpdateEligibleItems(operationType));
    })

    $('body').on('click', '.bulk-paid-feature-bump-button', function (event) {
        event.preventDefault();
        const selectedAds = getSelectedPaidFeatureSourceAds();
        const items = getPaidFeatureBumpEligibleItems(true);
        if (!items.length) {
            displayError(getBulkPaidFeatureIneligibleMessage(selectedAds));
            return;
        }
        tryOpenPlacementOperationPage({
            operation_type: PLACEMENT_OPERATION_BUMP_ADS,
            execution_mode: 'now',
            items,
            allow_scheduled: true,
        });
    })

    $('body').on('click', '.bulk-paid-feature-advertising-button', function (event) {
        event.preventDefault();
        const selectedAds = getSelectedPaidFeatureSourceAds();
        const items = getPaidFeatureAdvertisingEligibleItems(true);
        if (!items.length) {
            displayError(getBulkPaidFeatureIneligibleMessage(selectedAds));
            return;
        }
        tryOpenPlacementOperationPage({
            operation_type: PLACEMENT_OPERATION_ADVERTISE_ADS,
            execution_mode: 'now',
            items,
            allow_scheduled: true,
        });
    })

    $('body').on('click', '.bulk-price-update-button', function (event) {
        event.preventDefault();
        if (!getSelectedAdvertItems().length) {
            displayError(i18next.t('ads:priceUpdate.selectAdsRequired', {defaultValue: 'Select at least one advert.'}));
            return;
        }
        clearPriceUpdateErrors('#bulk-price-update-modal');
        $('#bulk-price-update-value').val('');
        setPriceUpdateSelectValue('#bulk-price-update-operation', 'increase_amount');
        setPriceUpdateSelectValue('#bulk-price-update-execution-mode', 'now');
        resetPriceUpdateDatePicker('#bulk-price-update-scheduled-at');
        $('#bulk-price-update-modal').modal('show');
    })

    $('body').on('change', '.price-update-execution-mode', function () {
        const modal = $(this).closest('.modal');
        modal.find('.price-update-scheduled-row').toggleClass('d-none', $(this).val() !== 'scheduled');
        if ($(this).val() !== 'scheduled') {
            resetPriceUpdateDatePicker(modal.find('.price-update-scheduled-at'));
        }
    })

    $('body').on('submit', '#bulk-price-update-form', handleBulkPriceUpdateSubmit);
    $('body').on('submit', '#status-update-form', handleStatusUpdateSubmit);
    $('body').on('click', '#status-update-modal .placement-plan-filter', function () {
        statusPlacementState.filter = $(this).data('placement-filter') || 'all';
        renderStatusPlacementPlan();
    });
    $('body').on('click', '#status-update-modal .placement-plan-exclude-item', function () {
        const itemKey = String($(this).data('placement-item-key') || '');
        if (itemKey && !statusPlacementState.excludedItemKeys.includes(itemKey)) {
            statusPlacementState.excludedItemKeys.push(itemKey);
        }
        loadStatusPlacementPlan().catch((error) => {
            renderWorkspaceWarning(
                $('#status-update-modal .status-update-placement-plan'),
                formatCopyToDraftError(error.errors || error),
            );
        });
    });
    $('body').on('click', '#status-update-modal .status-update-refresh-plan', function () {
        const button = this;
        withButtonPending(button, () => loadStatusPlacementPlan()).catch((error) => {
            renderWorkspaceWarning(
                $('#status-update-modal .status-update-placement-plan'),
                formatCopyToDraftError(error.errors || error),
            );
        });
    });
    $('body').on('change', '#status-update-execution-mode, #status-update-scheduled-at', function () {
        if ($('#status-update-operation-type').val() === 'activate' && statusPlacementState.plan) {
            loadStatusPlacementPlan().catch((error) => {
                renderWorkspaceWarning(
                    $('#status-update-modal .status-update-placement-plan'),
                    formatCopyToDraftError(error.errors || error),
                );
            });
        }
    });

    $("#bot-presence-select").on("change", function (event) {
        console.log("#bot-presence-select");
        let botFilter = document.querySelector(selectors.botIdsFilter);
        const rawValue = $(this).val();
        const selected = Array.isArray(rawValue)
            ? rawValue
            : String(rawValue || "")
                .split(",")
                .map(item => item.trim())
                .filter(Boolean);

        if (selected.includes('withoutBot')) {
            botFilter.reset();
            botFilter.disable();
        } else {
            botFilter.enable();
        }
        ;
        updateOlxFilterSummary();
        processAds(event);
    })

    $(".bot-select").on("change", function (event) {
        updateOlxFilterSummary();
        processAds(event);
    })

    $(".catfilter").on("change", function (event) {
        updateOlxFilterSummary();
        processAds(event);
    })

    $(".locationfilter").on("change", function (event) {
        updateOlxFilterSummary();
        processAds(event);
    })

    $("#search").on("input", function (event) {
        let clearBtn = $(this).siblings('.input-clear-button');
        clearBtn.removeClass('invisible');
        clearBtn.addClass('visible');
        if ($(this).val() !== '') {
            clearBtn.removeClass('invisible');
            clearBtn.addClass('visible');
        } else {
            clearBtn.removeClass('visible');
            clearBtn.addClass('invisible');
        }
        ;
        updateOlxFilterSummary();
        processAds(event);
    })

    $('.input-clear-button').on('click', function (event) {
        $(this).siblings('#search').val('').focus();
        $(this).removeClass('visible');
        $(this).addClass('invisible');
        updateOlxFilterSummary();
        processAds(event);
    })

    $("#newBotFilter2").on("change", function (event) {

        const selected = $(this).val();
        const button = $("#connectButton");
        if (selected) {
            button.prop('disabled', false)
        } else {
            button.prop('disabled', true)
        }

    })

    $("#limit-select li").on("click", function (event) {
        if ($(this).hasClass('page-limit-selected')) {
            return;
        }

        $("#limit-select li").removeClass('page-limit-selected');
        $(this).addClass('page-limit-selected');
        processAds(event);
    })

    $(`${selectors.bulkContainer} .bulk-label input, ${selectors.bulkContainer} .ads-mobile-bulk-master-checkbox`).on("change", function (event) {
        const checked = ($(this).is(':checked'))
        $(selectors.adsList + ' input[type="checkbox"]').each(function () {
            $(this).prop('checked', checked);
        });
        handleCheckboxes();
    })

    $('#connectBotModal').on('show.bs.modal', function (event) {

        const button = $(event.relatedTarget) // Button that triggered the modal
        ads_config.dataToConnect = [];

        let descriptionContent = '';
        if (button.hasClass('bulk-button')) {
            ads_config.dataToConnect = ads_config.dataToConnectBulk;

        } else {
            const ad_id = button.data('inventory-id') // Extract info from data-* attributes
            const account_id = button.data('account-id') // Extract info from data-* attributes
            ads_config.dataToConnect.push({ad_id: ad_id, account_id: account_id});
        }


        let modal = $(this);
        let description = modal.find('#modal-description');
        let spinner = modal.find('.spinner-border');
        let wrapper = modal.find('#botWrapper');
        let noBots = modal.find('#noBots');
        let noConnections = modal.find('#noConnections');
        let lowConnections = modal.find('#lowConnections');
        let footer = modal.find('.modal-footer');
        let html = ``;
        ads_config.emptyConnections = [];
        noBots.hide();
        noConnections.hide();
        lowConnections.hide();
        description.hide();
        footer.hide();
        wrapper.hide();
        spinner.show();
        let botFilter = document.querySelector('#newBotFilter2');

        const toConnectCount = ads_config.dataToConnect.length;

        descriptionContent = i18next.t('ads:events.connectBot.ads', {count: toConnectCount}); // `Вы хотите подключить бот к <strong>${toConnectCount}</strong> объявлениям`;

        description.empty();
        description.append(descriptionContent);

        getConnections().then(connections => {
            ads_config.emptyConnections = connections.connections.filter(c => c.bot_id === '');

            if (!ads_config.emptyConnections.length > 0) {
                spinner.hide();

                noConnections.show();
                return
            }
            ;

            if (ads_config.emptyConnections.length < ads_config.dataToConnect.length) {
                spinner.hide();

                const adsCount = ads_config.dataToConnect.length;
                const emptyConnectionsCount = ads_config.emptyConnections.length;

                const adsText = i18next.t('ads:events.connectBot.ads', {count: adsCount});
                const connectionText = i18next.t('ads:events.connectBot.connection', { count: emptyConnectionsCount });

                let text = `${adsText}, ${connectionText}`; // `Вы хотите подключить бот к ${ads_config.dataToConnect.length} объявлениям, но у вас есть всего ${ads_config.emptyConnections.length} подключений`


                lowConnections.find('.message').text(text);
                lowConnections.show();
                return
            }



            getSimpleBots().then(bots => {
                spinner.hide();

                if (!bots.bots.length > 0) {
                    noBots.show();
                    return
                }

                description.show();
                wrapper.show();
                let options = bots.bots.map(obj => {
                    return {'label': obj.name, 'value': obj.id}
                });
                botFilter.setOptions(options);

                footer.show();


                //modal.find('#botFilter').text(bots.bots[0].name)
            })
        })


    })

    $('#disconnectBotModal').on('show.bs.modal', function (event) {
        const button = $(event.relatedTarget) // Button that triggered the modal
        let modal = $(this);
        let description = modal.find('#modal-description');
        description.empty();

        ads_config.dataToDisconnect = [];


        if (button.hasClass('bulk-button') || button.hasClass('orphaned-disconnect-all-btn')) {
            if (button.hasClass('orphaned-disconnect-all-btn')) {
                ads_config.dataToDisconnectBulk = getOrphanedDisconnectData();
            }
            ads_config.dataToDisconnect = ads_config.dataToDisconnectBulk;

        } else {
            const connection_id = button.data('connection-id') // Extract info from data-* attributess
            const ad_id = button.data('inventory-id') // Extract info from data-* attributess
            ads_config.dataToDisconnect.push({connection_id: connection_id, ad_id: ad_id});
        }

        let descriptionContent = '';
        const toDisconnectCount = ads_config.dataToDisconnect.length;

        descriptionContent = i18next.t('ads:events.disconnectBot.ads', {count: toDisconnectCount})//`Вы действительно хотите отключить бот от объявления?``Вы действительно хотите отключить ботов от <strong>${toDisconnectCount}</strong> объявлений?`
        description.append(descriptionContent);
    })

    $('#connectButton').click(function (event) {
        const csrftoken = getCookie('csrftoken');
        let button = $(event.relatedTarget);

        const selected_bot = document.querySelector('#newBotFilter2').value;
        if (!selected_bot) {
            const translation = i18next.t('ads:events.botNotSelectedError') // Bot not selected
            displayError(translation)
            return
        }
        ;

        if (ads_config.emptyConnections.length === 0) {
            const translation = i18next.t('ads:events.noEmptyConnectionsError') // No empty connections
            displayError(translation)
            return
        }
        ;

        if (ads_config.emptyConnections.length < ads_config.dataToConnect.length) {
            const translation = i18next.t('ads:events.noEnoughEmptyConnectionsError') // Not enough empty connections
            displayError(translation)
            return
        }
        ;
        let sortedEmptyConnections = sortConnections(ads_config.emptyConnections);
        let connections = [];
        for (const data of ads_config.dataToConnect) {
            let connection_id = sortedEmptyConnections.pop().connection_id;
            connections.push({
                account_id: data.account_id,
                ad_id: data.ad_id,
                connection_id: connection_id,
                bot_id: selected_bot
            })
        }
        ;


        $.ajax({
            url: urls.connect,
            type: "POST",
            headers: {
                'X-CSRFToken': csrftoken,
            },
            data: JSON.stringify({connections: connections}),
            contentType: 'application/json; charset=utf-8',
            dataType: 'json',
            success: async function (response) {
                await getBotsAndConnections();
                ads_config.olx_ads = refreshAdsBotConnections(ads_config.olx_ads, ads_config.all_bots, ads_config.all_connections);
                ads_config.all_ads = mergeAdsWithOrphaned(ads_config.olx_ads, ads_config.all_bots, ads_config.all_connections);
                displayResults(response);
                $('#connectBotModal').modal('hide');
                setBotsIdsFilter();
            },
            error: function (xhr, status, error) {
                console.log(error);
            }
        });


    })

    $('#disconnectButton').click(function (event) {
        const csrftoken = getCookie('csrftoken');
        let button = $(event.relatedTarget);
        if (!ads_config.dataToDisconnect.length > 0) {
            const translation = i18next.t('ads:events.nothingToDisconnectError') // Nothing to disconnect
            displayError(translation)
            return
        }
        ;
        console.log('#disconnectButton dataToDisconnect', ads_config.dataToDisconnect);

        $.ajax({
            url: urls.disconnect,
            type: "POST",
            headers: {
                'X-CSRFToken': csrftoken,
            },
            data: JSON.stringify({connections: ads_config.dataToDisconnect}),
            contentType: 'application/json; charset=utf-8',
            dataType: 'json',
            success: async function (response) {
                await getBotsAndConnections();
                ads_config.olx_ads = refreshAdsBotConnections(ads_config.olx_ads, ads_config.all_bots, ads_config.all_connections);
                ads_config.all_ads = mergeAdsWithOrphaned(ads_config.olx_ads, ads_config.all_bots, ads_config.all_connections);
                displayResults(response);
                $('#disconnectBotModal').modal('hide');
                setBotsIdsFilter();
                console.log(response);
            },
            error: function (xhr, status, error) {
                console.log(error);
            }
        });


    })


}

export function bindCheckboxes() {
    $(`${selectors.adsList} .inventory-checkbox`).off('change.adsInventory').on('change.adsInventory', function (event) {
        handleCheckboxes();
    })
};
